import * as React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { withRouter } from "react-router-dom";
import MUIAppBar from "@mui/material/AppBar";
import MUIBox from "@mui/material/Box";
import MUIToolbar from "@mui/material/Toolbar";
import MUITypography from "@mui/material/Typography";
import MUIIconButton from "@mui/material/IconButton";
import MUIButton from "@mui/material/Button";
import MUIMenu from "@mui/material/Menu";
import MUIMenuIcon from "@mui/icons-material/Menu";
import MUIMenuItem from "@mui/material/MenuItem";
import MUILink from "@mui/material/Link";
import MUIInputBase from "@mui/material/InputBase";
import MUIArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { styled } from "@mui/material/styles";
import MUICollapse from "@mui/material/Collapse";
import MUISwitch from "@mui/material/Switch";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUILogoutIcon from "@mui/icons-material/Logout";
import MUIInfoIcon from "@mui/icons-material/Info";
import MUIList from "@mui/material/List";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUISupportIcon from "@mui/icons-material/Support";
import MUIListItemText from "@mui/material/ListItemText";
import MUIListItemButton from "@mui/material/ListItemButton";
import MUIAccountCircleIcon from "@mui/icons-material/AccountCircle";
import MUISearchIcon from "@mui/icons-material/Search";
import MUIExpandLessIcon from "@mui/icons-material/ExpandLess";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MUIDarkModeIcon from "@mui/icons-material/DarkMode";
import { grey } from "@mui/material/colors";
import MUICachedIcon from "@mui/icons-material/Cached";
import MUIManageHistoryIcon from "@mui/icons-material/ManageHistory";

import Security from "../Utility/Security";
import Setting from "../Utility/Setting";
import logoIcon from "../Asset/Image/logoWhite.png";
import MainContext from "../Context/MainContext/MainContext";

import DialogOmniSearch from "./Dialog/DialogOmniSearch";
import DialogConfirm from "./Dialog/DialogConfirm";

/**
 * Pages configuration for the application navigation.
 * This is the single source of truth used by both AppBar and the Command Palette.
 *
 * @param {object} handlers - Optional click handlers for modal pages.
 * @param {object|null} [mainContext=null] - Unused; kept for call-site compatibility.
 * @returns {object} Pages configuration object
 */
export function getPagesConfig(handlers = {}, mainContext = null) {
  const basePagesConfig = {
    reports: {
      title: "Reports",
      subpages: [
        {
          title: "Home",
          href: "/reports/home",
          permission: "module.reportHome",
        },
        {
          title: "Sales Pipeline",
          href: "/reports/salesPipeline",
          permission: "module.salesPipeline",
        },
        {
          title: "Month at a Glance",
          href: "/reports/monthGlance",
          permission: "module.monthAtAGlance",
        },
        {
          title: "Metric",
          href: "/reports/metric",
          permission: "module.metric",
        },
        {
          title: "Order Timeline Audit",
          href: "/reports/orderTimelineAudit",
          permission: "module.orderTimelineAudit",
        },
      ],
    },
    tasks: { title: "Tasks", href: "/tasks" },
    inboundFiles: { title: "Faxes", href: "/inboundFiles" },
    operations: {
      title: "Operations",
      subpages: [
        { title: "Orders", href: "/order" },
        { title: "Patients", href: "/patient/search" },
        { title: "Recurring Orders", href: "/recurringOrders" },
      ],
    },
    sales: {
      title: "Sales",
      subpages: [
        {
          title: "Accounts",
          href: "/sales/accounts",
          permission: "module.myAccounts",
        },
        {
          title: "Patients",
          href: "/sales/patients",
          permission: "module.myPatients",
        },
        { title: "Activities", href: "/sales/activities" },
        {
          title: "PCR Links",
          href: "/sales/pcrLinks",
          permission: "module.salesPcrLinks",
        },
        {
          title: "Presidents Club",
          href: "/sales/presidentsClub",
          permission: "module.presidentsClub",
        },
        {
          title: "Bonus",
          href: "/sales/bonus",
          permission: "module.salesBonus",
        },
        {
          title: "Scorecard",
          href: "/sales/scorecard",
          permission: "module.salesScorecard",
        },
        {
          title: "In-Service Summary",
          href: "/sales/inServiceSummary",
          permission: "module.inServiceSummary",
        },
        {
          title: "Account Performance",
          href: "/reports/accountPerformance",
          permission: "module.accountPerformance",
        },
        {
          title: "Workflow - Sales Rep",
          href: "/dashboard/1",
          permission: "dashboard.access.1",
        },
        {
          title: "Workflow - Manager / VP",
          href: "/dashboard/2",
          permission: "dashboard.access.2",
        },
        {
          title: "Workflow - ATP",
          href: "/dashboard/4",
          permission: "dashboard.access.4",
        },
        {
          title: "Workflow - PCR",
          href: "/dashboard/5",
          permission: "dashboard.access.5",
        },
      ],
    },
    purchasing: {
      title: "Purchasing",
      subpages: [
        {
          title: "Purchase Queues",
          href: "/purchaseQueues",
          permission: "module.purchasingQueues",
        },
        {
          title: "Inventory Request",
          href: "#!",
          permission: "module.inventoryRequest",
          onClick: handlers.openInventoryRequestModal,
          excludeFromSearch: true,
        },
        {
          title: "PO Delivery Update",
          href: "#!",
          permission: "module.poDeliveryUpdate",
          onClick: handlers.openPODeliveryUpdateModal,
          excludeFromSearch: true,
        },
        { title: "Vendors", href: "/vendors", permission: "module.vendors" },
        {
          title: "Shipping Details Report",
          href: "/shippingDetails",
          permission: "module.shippingDetailsReport",
        },
        {
          title: "Products",
          href: "/products",
          permission: "module.productCategories",
        },
        {
          title: "Service Parts",
          href: "/serviceParts",
          permission: "module.manageServiceParts",
        },
        {
          title: "Service Part Categories",
          href: "/admin/servicePartCatalogCategories",
          permission: "module.servicePartCategories",
        },
      ],
    },
    fieldOperations: {
      title: "Field Ops",
      subpages: [
        { title: "Deliveries", href: "/deliveries" },
        { title: "ATP", href: "/atp" },
        { title: "Inventory Tracking", href: "/inventoryTracking" },
        {
          title: "Workflow",
          href: "/dashboard/3",
          permission: "dashboard.access.3",
        },
      ],
    },
    marketing: {
      title: "Marketing",
      subpages: [
        {
          title: "Accounts",
          href: "/marketing/accounts",
          permission: "module.marketingAccounts",
        },
        {
          title: "Campaigns",
          href: "/admin/marketingCampaigns",
          permission: "module.marketingCampaigns",
        },
        {
          title: "Campaign Categories",
          href: "/admin/marketingCampaignCategories",
          permission: "module.marketingCampaignCategories",
        },
      ],
    },
    revenueCycle: {
      title: "Revenue Cycle",
      subpages: [
        {
          title: "AR Dashboard",
          href: "/revenueCycle/ARDashboard",
          permission: "module.ARDashboard",
        },
        {
          title: "Invoice Production",
          href: "/reports/userProductionDetail/invoiceProduction",
          permission: "module.userProductionDetail.invoiceProduction",
        },
        {
          title: "Patient Collections",
          href: "/revenueCycle/patientCollections",
          permission: "module.operationsPatientCollections",
        },
        {
          title: "RTD Patient Collections",
          href: "/revenueCycle/rtdPatientCollections",
          permission: "module.operationsPatientCollections",
        },
        {
          title: "Workflow - Initial Billing",
          href: "/dashboard/6",
          permission: "dashboard.access.6",
        },
        {
          title: "Workflow - Collections",
          href: "/dashboard/7",
          permission: "dashboard.access.7",
        },
      ],
    },
    admin: {
      title: "Admin",
      subpages: [
        {
          title: "Users",
          href: "/admin/users",
          permission: "module.adminUsers",
          category: "Users",
        },
        {
          title: "Departments",
          href: "/admin/departments",
          permission: "module.adminDepartments",
          category: "Users",
        },
        {
          title: "Teams",
          href: "/admin/teams",
          permission: "module.adminTeams",
          category: "Users",
        },
        {
          title: "Roles",
          href: "/admin/roles",
          permission: "module.adminRoles",
          category: "Users",
        },
        {
          title: "Permissions",
          href: "/admin/permissions",
          permission: "module.adminPermissions",
          category: "Users",
        },

        {
          title: "Queues",
          href: "/admin/queues",
          permission: "module.adminQueues",
          category: "Queues & Workflows",
        },
        {
          title: "Queue Types",
          href: "/admin/queueTypes",
          permission: "module.adminQueueTypes",
          category: "Queues & Workflows",
        },
        {
          title: "Queue Routes",
          href: "/admin/queueRoutes",
          permission: "module.adminQueueRoutes",
          category: "Queues & Workflows",
        },
        {
          title: "Workflows",
          href: "/admin/dashboards",
          permission: "module.adminDashboards",
          category: "Queues & Workflows",
        },
        {
          title: "Widgets",
          href: "/admin/widgets",
          permission: "module.adminWidgets",
          category: "Queues & Workflows",
        },

        {
          title: "Issue Categories",
          href: "/admin/issueCategories",
          permission: "module.adminIssueCategories",
          category: "Patients",
        },
        {
          title: "Issue Reasons",
          href: "/admin/issueReasons",
          permission: "module.adminIssueReasons",
          category: "Patients",
        },

        {
          title: "Activity File Types",
          href: "/admin/activityFileTypes",
          permission: "module.adminActivityFileTypes",
          category: "Orders",
        },
        {
          title: "Order Status Reasons",
          href: "/admin/orderStatusReasons",
          permission: "module.adminStatusReasons",
          category: "Orders",
        },
        {
          title: "Order Status Reason Details",
          href: "/admin/orderStatusReasonDetails",
          permission: "module.adminStatusReasonDetails",
          category: "Orders",
        },
        {
          title: "Order Activity Types",
          href: "/admin/orderActivityTypes",
          permission: "module.adminOrderActivityTypes",
          category: "Orders",
        },
        {
          title: "Note Types",
          href: "/admin/noteTypes",
          permission: "module.adminNoteTypes",
          category: "Orders",
        },
        {
          title: "Providers",
          href: "/admin/providers",
          permission: "module.adminProviders",
          category: "Orders",
        },

        {
          title: "Account Types",
          href: "/admin/accountTypes",
          permission: "module.adminAccountTypes",
          category: "Sales",
        },
        {
          title: "Contact Types",
          href: "/admin/contactTypes",
          permission: "module.adminContactTypes",
          category: "Sales",
        },
        {
          title: "Credit Card Types",
          href: "/creditCardTypes",
          permission: "module.adminCreditCardTypes",
          category: "Sales",
        },
        {
          title: "Expense Types",
          href: "/expenseTypes",
          permission: "module.adminExpenseTypes",
          category: "Sales",
        },
        {
          title: "Commission Tiers",
          href: "/admin/commissionTiers",
          permission: "module.adminCommissionTiers",
          category: "Sales",
        },
        {
          title: "Preferred Contact Methods",
          href: "/admin/marketingPreferredContactMethods",
          permission: "module.marketingPreferredContactMethods",
          category: "Marketing",
        },

        {
          title: "Insurance Types",
          href: "/admin/insuranceTypes",
          permission: "module.adminPayorSources",
          category: "Insurance",
        },
        {
          title: "Insurance Subtypes",
          href: "/admin/insuranceSubTypes",
          permission: "module.adminInsurance",
          category: "Insurance",
        },
        {
          title: "Insurance Groups",
          href: "/admin/insuranceSubTypeGroups",
          permission: "module.adminInsuranceGroups",
          category: "Insurance",
        },

        {
          title: "Part Return Statuses",
          href: "/admin/serviceQuotePartStatuses",
          permission: "module.adminServiceQuotePartStatuses",
          category: "Purchasing",
        },
        {
          title: "Part Return Sources",
          href: "/admin/serviceQuotePartSources",
          permission: "module.adminServiceQuotePartSources",
          category: "Purchasing",
        },
        {
          title: "Part Return Reasons",
          href: "/admin/serviceQuotePartReasons",
          permission: "module.adminServiceQuotePartReasons",
          category: "Purchasing",
        },
        {
          title: "Base Unit Types",
          href: "/admin/baseUnitTypes",
          permission: "module.adminBaseUnitTypes",
          category: "Purchasing",
        },
        {
          title: "Base Units",
          href: "/admin/baseUnits",
          permission: "module.adminBaseUnits",
          category: "Purchasing",
        },
        {
          title: "Brightree Products",
          href: "/admin/btProducts",
          permission: "module.adminBrightreeProducts",
          category: "Purchasing",
        },
        {
          title: "Product Matrix Entries",
          href: "/productMatrixEntry",
          permission: "module.adminProductMatrixEntries",
          category: "Purchasing",
        },

        {
          title: "Business Support Request Resolutions",
          href: "/admin/businesssSupportRequestResolutions",
          permission: "module.adminBusinessSupportRequestResolutions",
          category: "Business Support",
        },

        {
          title: "Delivery Form Acknowledgements",
          href: "/deliveryAcknowledgements",
          permission: "module.adminDeliveryFormAcknowledgements",
          category: "Field Operations",
        },

        {
          title: "HCPCS Codes",
          href: "/admin/hcpcsCodes",
          permission: "module.adminHcpcsCodes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Diagnosis Codes",
          href: "/admin/diagnosisCodes",
          permission: "module.adminDiagnosisCodes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Modifier Codes",
          href: "/admin/modifierCodes",
          permission: "module.adminModifierCodes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Fee Schedule",
          href: "/admin/feeSchedules",
          permission: "module.adminFeeSchedule",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Place of Services",
          href: "/admin/placeOfServices",
          permission: "module.adminPlaceOfServices",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Distribution Reasons",
          href: "/admin/distributionReasons",
          permission: "module.adminDistributionReasons",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Distribution Reason Types",
          href: "/admin/distributionReasonTypes",
          permission: "module.adminDistributionReasonTypes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Distribution Types",
          href: "/admin/distributionTypes",
          permission: "module.adminDistributionTypes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Invoice Followup Statuses",
          href: "/admin/invoiceFollowupStatuses",
          permission: "module.adminInvoiceFollowupStatuses",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Brightree Invoice Statuses",
          href: "/admin/brightreeInvoiceStatuses",
          permission: "module.adminBrightreeInvoiceStatuses",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Brightree Stop/Hold Types",
          href: "/admin/brightreeStopHoldTypes",
          permission: "module.adminBrightreeStopHoldTypes",
          category: "Revenue Cycle & Finance",
        },
        {
          title: "Brightree Stop/Hold Reasons",
          href: "/admin/brightreeStopHoldReasons",
          permission: "module.adminBrightreeStopHoldReasons",
          category: "Revenue Cycle & Finance",
        },

        {
          title: "Notification Statuses",
          href: "/admin/notificationStatuses",
          permission: "module.adminNotificationStatuses",
          category: "Notifications",
        },
        {
          title: "Notification Types",
          href: "/admin/notificationTypes",
          permission: "module.adminNotificationTypes",
          category: "Notifications",
        },
        {
          title: "Notification Channels",
          href: "/admin/notificationChannels",
          permission: "module.adminNotificationChannels",
          category: "Notifications",
        },

        {
          title: "Terms",
          href: "/admin/terms",
          permission: "module.adminTerms",
          category: "System",
        },
        {
          title: "Locations",
          href: "/admin/locations",
          permission: "module.adminLocations",
          category: "System",
        },
        {
          title: "Regions",
          href: "/admin/regions",
          permission: "module.adminRegions",
          category: "System",
        },
        {
          title: "User Production Types",
          href: "/admin/userProductionTypes",
          permission: "module.adminUserProductionTypes",
          category: "System",
        },
        {
          title: "User Production Detail Types",
          href: "/admin/userProductionDetailTypes",
          permission: "module.adminUserProductionDetailTypes",
          category: "System",
        },
        {
          title: "Time Zones",
          href: "/admin/timeZones",
          permission: "module.adminTimeZones",
          category: "System",
        },
        // { title: "Gender Identities", href: "/admin/genderIdentities", permission: "module.adminGenderIdentities", category: "System" },
        // { title: "Preferred Pronouns", href: "/admin/preferredPronouns", permission: "module.adminPreferredPronouns", category: "System" },

        {
          title: "Manual Document Builder",
          href: "/manualDocumentBuilder",
          permission: "module.adminManualDocumentBuilder",
          category: "Administrative",
        },
        {
          title: "Mass Route",
          href: "/admin/massRoute",
          permission: "module.adminMassRoute",
          category: "Administrative",
        },
      ],
    },
  };

  return basePagesConfig;
}

class AppBar extends React.Component {
  static contextType = MainContext;

  constructor(props) {
    super(props);

    this.state = {
      openItems: {},
      viewMode: "desktop",
      isSearching: false,
      dialogResetPreferencesOpen: false,
      pages: getPagesConfig({
        openInventoryRequestModal: props.openInventoryRequestModal,
        openPODeliveryUpdateModal: props.openPODeliveryUpdateModal,
      }),
    };

    // Refs to track the width of each element
    this.logoRef = React.createRef();
    this.searchBoxRef = React.createRef();
    this.navBoxRef = React.createRef();
    this.logoutRef = React.createRef();
    this.hamburgerMenuRef = React.createRef();

    // Initialize debounce variables
    this.resizeTimeout = null;
  }

  // Function to update the view mode dynamically based on element widths and window width
  updateViewModeDynamicallyBasedOnBrowserAndElementWidths = () => {
    const getElementWidth = (elementRef) => {
      if (elementRef && elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        return rect.width;
      }
      return 0;
    };

    // Get the widths of the elements using getBoundingClientRect()
    // Hamburger may not be in DOM when in desktop mode (e.g. on refresh), so use fallback
    const logoWidth = getElementWidth(this.logoRef);
    const searchBoxWidth = getElementWidth(this.searchBoxRef);
    const navBoxWidth = getElementWidth(this.navBoxRef);
    const logoutWidth = getElementWidth(this.logoutRef);
    const hamburgerMenuWidth = getElementWidth(this.hamburgerMenuRef) || 48; // Fallback width for hamburger menu when not rendered (e.g. in desktop mode on initial load)

    // Get the total width of the elements inside the AppBar for different view modes
    const totalDesktopViewWidth =
      logoWidth + searchBoxWidth + navBoxWidth + logoutWidth + 70; // Add extra padding/margin
    const totalTabletViewWidth =
      hamburgerMenuWidth + logoWidth + searchBoxWidth + logoutWidth + 50; // Add extra padding/margin
    // const totalMobileViewWidth = hamburgerMenuWidth + searchBoxWidth + logoutWidth + 50;

    const windowWidth = window.innerWidth;

    let newViewMode = null;
    if (windowWidth >= totalDesktopViewWidth) {
      newViewMode = "desktop";
    } else if (windowWidth >= totalTabletViewWidth) {
      newViewMode = "tablet";
    } else {
      newViewMode = "mobile";
    }

    if (newViewMode !== this.state.viewMode) {
      this.setState({ viewMode: newViewMode });
    }
  };

  handleResize = () => {
    // Set a new timeout to call updateViewModeDynamicallyBasedOnBrowserAndElementWidths after 200ms
    this.resizeTimeout = setTimeout(
      this.updateViewModeDynamicallyBasedOnBrowserAndElementWidths,
      200,
    );
  };

  componentDidMount() {
    // Add resize event listener
    window.addEventListener("resize", this.handleResize);
    this.updateViewModeDynamicallyBasedOnBrowserAndElementWidths();
  }

  componentDidUpdate(prevState) {
    if (prevState.viewMode !== this.state.viewMode) {
      this.updateViewModeDynamicallyBasedOnBrowserAndElementWidths();
    }
  }

  componentWillUnmount() {
    // Cleanup event listener and clear timeout on unmount
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  /**
   * Builds the pages configuration for the AppBar from the current props
   * (modal handlers).
   *
   * @returns {object} The pages configuration object returned by
   * {@link getPagesConfig}.
   */
  getPagesConfigForCurrentUser() {
    return getPagesConfig(
      {
        openInventoryRequestModal: this.props.openInventoryRequestModal,
        openPODeliveryUpdateModal: this.props.openPODeliveryUpdateModal,
      },
      this.context,
    );
  }

  render() {
    const pagesConfig = this.getPagesConfigForCurrentUser();
    const hamburgerMenuPageDescriptors = [
      { title: "Dashboard", href: "/dashboard" },
      ...Object.values(pagesConfig),
    ];
    const Search = styled("div")(({ theme }) => ({
      position: "relative",
      borderRadius: "4px",
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
      width: "170px",
    }));

    const SearchIconWrapper = styled("div")(() => ({
      paddingLeft: 10,
      height: "100%",
      position: "absolute",
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }));

    const StyledInputBase = styled(MUIInputBase)(({ theme }) => ({
      width: "100%",
      "& .MuiInputBase-input": {
        padding: 5,
        paddingLeft: 40,
        color: theme.palette.text.primary,
      },
      "& .MuiInputBase-input::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    }));

    return (
      <MUIAppBar
        position="fixed"
        sx={{
          zIndex: 1040,
        }}
        onClick={() => {
          if (this.props.modalOpen) {
            this.props.onCloseDialog();
          }
        }}
      >
        <MUIToolbar
          variant="dense"
          disableGutters
          sx={
            this.state.viewMode === "desktop"
              ? { justifyContent: "flex-start", ml: 2 }
              : { justifyContent: "flex-start" }
          }
        >
          {/* Hamburger Menu for Tablet and Mobile Screens */}
          {(this.state.viewMode === "tablet" ||
            this.state.viewMode === "mobile") && (
            <MUIBox ref={this.hamburgerMenuRef}>
              <MUIIconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={(e) =>
                  this.setState({ anchorElHamburgerMenuNav: e.currentTarget })
                }
                color="inherit"
              >
                <MUIMenuIcon />
              </MUIIconButton>
              <MUIMenu
                anchorEl={this.state.anchorElHamburgerMenuNav}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                open={Boolean(this.state.anchorElHamburgerMenuNav)}
                onClose={() =>
                  this.setState({
                    anchorElHamburgerMenuNav: null,
                    openItems: {},
                  })
                }
              >
                {hamburgerMenuPageDescriptors.map(
                  this.renderHamburgerMenuNavItems,
                )}
              </MUIMenu>
            </MUIBox>
          )}

          {/* Logo for Desktop and Tablet Screens */}
          <MUIBox
            ref={this.logoRef}
            sx={
              this.state.viewMode === "tablet" ||
              this.state.viewMode === "desktop"
                ? { display: "flex" }
                : { position: "absolute", visibility: "hidden" }
            }
          >
            <ReactRouterLink
              to="/dashboard"
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img src={logoIcon} alt="Sales Pilot Logo" />
              <MUITypography variant="h6" noWrap>
                Sales Pilot
              </MUITypography>
            </ReactRouterLink>
          </MUIBox>

          {/* Search Box for All Screen Sizes */}
          <MUIBox
            ref={this.searchBoxRef}
            sx={{
              display: "flex",
              alignItems: "center",
              marginRight: 2,
              marginLeft: 3,
            }}
          >
            <Search
              sx={{
                backgroundColor: (theme) => theme.palette.background.default,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.default,
                },
              }}
            >
              <SearchIconWrapper>
                <MUISearchIcon
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                />
              </SearchIconWrapper>
              <StyledInputBase
                fullWidth
                placeholder="Search..."
                inputProps={{ "aria-label": "search" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    this.setState({
                      isSearching: true,
                      dialogOmniSearchOpen: true,
                      omniSearchQuery: e.target.value,
                    });
                  }
                }}
              />
              <DialogOmniSearch
                open={this.state.dialogOmniSearchOpen}
                onClose={() => this.setState({ dialogOmniSearchOpen: false })}
                searchQuery={this.state.omniSearchQuery}
                loading={this.state.isSearching}
                history={this.props.history}
              />
            </Search>
          </MUIBox>

          {/* Navigation Links for Desktop Screens */}
          <MUIBox
            ref={this.navBoxRef}
            sx={
              this.state.viewMode === "desktop"
                ? { display: "flex", justifyContent: "space-between" }
                : {
                    position: "absolute",
                    visibility: "hidden",
                    display: "flex",
                    justifyContent: "space-between",
                  }
            }
          >
            {Object.keys(pagesConfig).map((pageKey) =>
              this.renderNavItem(pageKey, pagesConfig),
            )}
          </MUIBox>

          {/* Logout Button for All Screen Sizes */}
          <MUIBox
            sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
          >
            {this.renderLogoutNavMenu()}
          </MUIBox>
        </MUIToolbar>
      </MUIAppBar>
    );
  }

  filterSubpages(subpages) {
    return subpages
      .map((subpage) => ({
        ...subpage,
        // Recursively filter any nested subpages
        subpages: subpage.subpages
          ? this.filterSubpages(subpage.subpages)
          : undefined,
      }))
      .filter(
        (subpage) =>
          !subpage.permission ||
          Security.hasPermission(subpage.permission) ||
          (subpage.subpages && subpage.subpages.length > 0),
      ); // Keep subpages without permissions or with accessible nested subpages
  }

  renderNavItem(pageKey, pagesConfig) {
    const page = pagesConfig[pageKey];
    const isDropdown = page.subpages && page.subpages.length > 0;

    // Check permissions for non-dropdown pages
    const hasPermission =
      !page.permission || Security.hasPermission(page.permission);

    // If it's a dropdown, filter subpages based on permissions
    const filteredSubpages = isDropdown
      ? this.filterSubpages(page.subpages)
      : [];

    // If the page has no accessible subpages, remove the title
    const shouldRenderTitle = isDropdown && filteredSubpages.length > 0;

    // If the page is not a dropdown and no permission, don't render it
    if (!isDropdown && !hasPermission) {
      return null;
    }

    const menuItems = [];
    let currentCategory = null;

    filteredSubpages.forEach((subpage) => {
      // If the category of the current subpage is different from the previous one
      if (subpage.category && subpage.category !== currentCategory) {
        // Add a category header
        menuItems.push(
          <MUIMenuItem
            key={`${subpage.category}-header`}
            sx={{ pointerEvents: "none", pl: 2 }}
          >
            <MUITypography
              variant="subtitle2"
              sx={{
                padding: "6px 0",
                color: grey[500],
                textAlign: "left",
                textTransform: "uppercase",
                display: "inline-block",
                borderBottom: `1px solid ${grey[500]}`,
                width: "100%",
              }}
            >
              {subpage.category}
            </MUITypography>
          </MUIMenuItem>,
        );
        // Update the current category
        currentCategory = subpage.category;
      }

      // Check if the subpage has its own subpages
      const hasSubsubpages = subpage.subpages && subpage.subpages.length > 0;

      // Render subpage title
      if (hasSubsubpages) {
        menuItems.push(
          <MUIMenuItem
            key={subpage.title}
            sx={{ pointerEvents: "none", pl: 2 }}
          >
            <MUITypography sx={{ textAlign: "left" }}>
              {subpage.title}
            </MUITypography>
          </MUIMenuItem>,
        );
      } else {
        menuItems.push(
          <ReactRouterLink
            to={subpage.href}
            style={{ textDecoration: "none", color: "inherit" }}
            key={subpage.title}
            onClick={subpage.onClick ? subpage.onClick : null}
          >
            <MUIMenuItem onClick={() => this.setState({ [pageKey]: null })}>
              <MUITypography sx={{ textAlign: "center" }}>
                {subpage.title}
              </MUITypography>
            </MUIMenuItem>
          </ReactRouterLink>,
        );
      }

      // If the subpage has sub-subpages, render them
      if (hasSubsubpages) {
        const filteredSubsubpages = this.filterSubpages(subpage.subpages);
        filteredSubsubpages.forEach((subsubpage) => {
          menuItems.push(
            <ReactRouterLink
              to={subsubpage.href}
              style={{ textDecoration: "none", color: "inherit" }}
              key={subsubpage.title}
            >
              <MUIMenuItem
                onClick={() => this.setState({ [pageKey]: null })}
                sx={{ pl: 4 }}
              >
                <MUITypography sx={{ textAlign: "left" }}>
                  {subsubpage.title}
                </MUITypography>
              </MUIMenuItem>
            </ReactRouterLink>,
          );
        });
      }
    });

    return isDropdown ? (
      <div key={page.title}>
        {shouldRenderTitle && (
          <MUIButton
            color="inherit"
            size="small"
            sx={{ pl: 1 }}
            onClick={(e) => this.setState({ [pageKey]: e.currentTarget })}
            endIcon={<MUIArrowDropDownIcon />}
          >
            <MUITypography variant="button" sx={{ whiteSpace: "nowrap" }}>
              {page.title}
            </MUITypography>
          </MUIButton>
        )}
        <MUIMenu
          anchorEl={this.state[pageKey]}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          keepMounted
          open={Boolean(this.state[pageKey])}
          onClick={() => this.setState({ [pageKey]: null })}
          sx={{
            display: { xs: "none", lg: "block" },
            mt: "45px",
            maxHeight: "550px",
          }}
        >
          {menuItems}
        </MUIMenu>
      </div>
    ) : (
      hasPermission && ( // Render non-dropdown page only if the user has permission
        <ReactRouterLink
          to={page.href}
          key={page.title}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <MUIButton color="inherit" size="small">
            <MUITypography variant="button">{page.title}</MUITypography>
          </MUIButton>
        </ReactRouterLink>
      )
    );
  }

  // Helper function to render menu items
  renderHamburgerMenuNavItems = (page) => {
    const hasPermission =
      !page.permission || Security.hasPermission(page.permission);
    if (!hasPermission) return null; // Skip pages without permission

    const isDropdown = page.subpages && page.subpages.length > 0;
    const hasAccessibleSubpages =
      isDropdown &&
      page.subpages.some(
        (subpage) =>
          !subpage.permission || Security.hasPermission(subpage.permission),
      );

    if (isDropdown && !hasAccessibleSubpages) return null; // Skip dropdown titles without accessible subpages

    // Toggles the open state of the specific item
    const handleToggleCollapse = (pageTitle) => {
      this.setState((prevState) => {
        return {
          openItems: {
            [pageTitle]: !prevState.openItems[pageTitle], // Toggle the state of the clicked item
          },
        };
      });
    };

    const renderCategoryHeader = (category) => {
      return (
        <MUIListItemButton sx={{ pointerEvents: "none", pl: 2 }}>
          <MUITypography
            variant="subtitle2"
            sx={{
              padding: "6px 0",
              color: grey[500],
              textAlign: "left",
              textTransform: "uppercase",
              display: "inline-block",
              borderBottom: `1px solid ${grey[500]}`,
              width: "100%",
            }}
          >
            {category}
          </MUITypography>
        </MUIListItemButton>
      );
    };

    const renderSubpages = (subpages) => {
      let lastCategory = null; // Track the last rendered category
      return subpages.map((subpage, index) => {
        // Skip subpages without permission
        const hasSubpagePermission =
          !subpage.permission || Security.hasPermission(subpage.permission);
        if (!hasSubpagePermission) return null;

        const category = subpage.category;

        // Render the category header only if it has changed (or if it's the first subpage with that category)
        const renderCategory = category && category !== lastCategory;
        if (renderCategory) {
          lastCategory = category; // Update last rendered category
        }

        // Determine padding for the subpage: first subpage in category should not be indented
        const paddingLeft = 3;

        return (
          <React.Fragment key={subpage.title}>
            {renderCategory && renderCategoryHeader(category)}
            <ReactRouterLink
              to={subpage.href}
              style={{ textDecoration: "none", color: "inherit" }}
              onClick={subpage.onClick ? subpage.onClick : null}
            >
              <MUIListItemButton
                sx={{ pl: paddingLeft }}
                onClick={() =>
                  this.setState({
                    anchorElHamburgerMenuNav: null,
                    openItems: {},
                  })
                }
              >
                <MUIListItemText primary={subpage.title} />
              </MUIListItemButton>
            </ReactRouterLink>
          </React.Fragment>
        );
      });
    };

    return (
      <div key={page.title}>
        {isDropdown && hasAccessibleSubpages && (
          <MUIList component="div" disablePadding>
            <MUIListItemButton
              sx={{ minWidth: "300px" }}
              onClick={() => handleToggleCollapse(page.title)}
            >
              <MUIListItemText primary={page.title} />
              {this.state.openItems[page.title] ? (
                <MUIExpandLessIcon />
              ) : (
                <MUIExpandMoreIcon />
              )}
            </MUIListItemButton>
            <MUICollapse
              in={this.state.openItems[page.title]}
              timeout="auto"
              unmountOnExit
            >
              <MUIList component="div" disablePadding>
                {renderSubpages(page.subpages)}{" "}
                {/* Render subpages with category headers */}
              </MUIList>
            </MUICollapse>
          </MUIList>
        )}
        {!isDropdown && (
          <ReactRouterLink
            to={page.href}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <MUIMenuItem
              onClick={() =>
                this.setState({ anchorElHamburgerMenuNav: null, openItems: {} })
              }
            >
              <MUITypography sx={{ textAlign: "center", padding: "4px 0" }}>
                {page.title}
              </MUITypography>
            </MUIMenuItem>
          </ReactRouterLink>
        )}
      </div>
    );
  };

  renderLogoutNavMenu() {
    const logoutMenuItems = {
      title: "Settings",
      subpages: [
        {
          title: "Log Out",
          href: "/logout",
          icon: <MUILogoutIcon />,
          onClick: "logout",
        },
        {
          title: `Update Notes (${window.salesPilot.meta.version})`,
          href: "#!",
          icon: <MUIInfoIcon />,
          onClick: "updateNotes",
        },
        {
          title: `Support`,
          href: "#!",
          icon: <MUISupportIcon />,
          onClick: "support",
        },
        {
          title: "Reload Data",
          href: "#!",
          icon: <MUICachedIcon />,
          onClick: "reloadData",
        },
        {
          title: "Reset Preferences",
          href: "#!",
          icon: <MUIManageHistoryIcon />,
          onClick: "resetPreferences",
        },
        {
          title: "Dark Mode",
          href: "#!",
          icon: <MUIDarkModeIcon />,
          onClick: null,
          darkModeSwitch: true,
        },
      ],
    };

    return (
      <MUIBox ref={this.logoutRef}>
        {/* Icon Button that triggers the menu */}
        <MUIIconButton
          color="inherit"
          size="small"
          onClick={(e) => this.setState({ anchorElMenu: e.currentTarget })}
          sx={{
            "&:hover": {
              borderRadius: "50%",
            },
            width: "50px",
            height: "50px",
          }}
        >
          <MUILink underline="none">
            <MUIAccountCircleIcon sx={{ fontSize: "30px" }} />
          </MUILink>
        </MUIIconButton>

        {/* Menu Component that is displayed when the icon button is clicked */}
        <MUIMenu
          anchorEl={this.state.anchorElMenu}
          anchorOrigin={{
            horizontal: "left",
            vertical: "top",
          }}
          keepMounted
          transformOrigin={{
            horizontal: "left",
            vertical: "top",
          }}
          open={Boolean(this.state.anchorElMenu)}
          onClose={() => this.setState({ anchorElMenu: null })}
          sx={{ display: { xs: "block", lg: "block" }, mt: "45px" }}
        >
          {/* Iterate over the subpages in the 'settingsMenu' */}
          {logoutMenuItems.subpages.map((item) => (
            <div key={item.title}>
              {/* Render items that are links (e.g., Logout, Update Notes) */}
              {!item.darkModeSwitch ? (
                <ReactRouterLink
                  to={item.href}
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={() => {
                    if (item.onClick === "logout") {
                      window.sessionStorage.clear();
                      window.location.href = "/";
                    } else if (item.onClick === "updateNotes") {
                      window.open(
                        "https://rehabmedicalindy.sharepoint.com/:fl:/g/contentstorage/CSP_ced075ff-5a95-4265-b31d-063781f34c81/EXlxmOHnowFItfr-Z5m56pMBi7-IwGayy_5mEHLAr__d-g?e=oZaGSr&nav=cz0lMkZjb250ZW50c3RvcmFnZSUyRkNTUF9jZWQwNzVmZi01YTk1LTQyNjUtYjMxZC0wNjM3ODFmMzRjODEmZD1iJTIxXzNYUXpwVmFaVUt6SFFZM2dmTk1nZUZVX0dmSDFaeEZxbUIxQ0NOT1RjV2RrdThmYVlmM1FJWFhCUkpQN29GciZmPTAxM0tVUTdFVFpPR01PRFo1REFGRUxMNlg2TTZNM1QyVVQmYz0lMkYmYT1Mb29wQXBwJnA9JTQwZmx1aWR4JTJGbG9vcC1wYWdlLWNvbnRhaW5lciZ4PSU3QiUyMnclMjIlM0ElMjJUMFJUVUh4eVpXaGhZbTFsWkdsallXeHBibVI1TG5Ob1lYSmxjRzlwYm5RdVkyOXRmR0loWHpOWVVYcHdWbUZhVlV0NlNGRlpNMmRtVGsxblpVWlZYMGRtU0RGYWVFWnhiVUl4UTBOT1QxUmpWMlJyZFRobVlWbG1NMUZKV0ZoQ1VrcFFOMjlHY253d01UTkxWVkUzUlZOTVJVazBUMDVOVGxnMVJrZExVazFaV0VKRVRqUkhVMDFFJTIyJTJDJTIyaSUyMiUzQSUyMmNiM2QzMWU4LWRkODEtNDYwMi04OTA4LWVjZmFkZDNkMWZiZSUyMiU3RA%3D%3D",
                      );
                    } else if (item.onClick === "support") {
                      window.open(
                        "https://rehabmedical.desk365.io/support/form/salespilot/",
                      );
                    } else if (item.onClick === "reloadData") {
                      // Clears loaded dictionaries and refreshes so they reload.
                      window.sessionStorage.clear();
                      window.location.reload();
                    } else if (item.onClick === "resetPreferences") {
                      this.setState({
                        anchorElMenu: null,
                        dialogResetPreferencesOpen: true,
                      });
                    }
                  }}
                >
                  <MUIMenuItem
                    onClick={() => this.setState({ anchorElMenu: null })}
                  >
                    <MUIListItemIcon>{item.icon}</MUIListItemIcon>
                    <MUIListItemText primary={item.title} />
                  </MUIMenuItem>
                </ReactRouterLink>
              ) : null}

              {/* Render Dark Mode switch */}
              {item.darkModeSwitch && (
                <MUIBox sx={{ display: "flex", alignItems: "center" }}>
                  <MUIListItemIcon sx={{ minWidth: "36px", ml: 2 }}>
                    {item.icon} {/* Dark Mode Icon */}
                  </MUIListItemIcon>
                  <MUIFormControlLabel
                    sx={{
                      mb: 0,
                      ml: 1, // Add margin to align better with text
                      display: "flex",
                      justifyContent: "flex-start",
                      margin: "0 0", // Remove margin around the label
                    }}
                    control={
                      <MUISwitch
                        sx={{ ml: 3 }}
                        checked={Setting.get("ui.theme") === "dark"}
                        onChange={(e) => {
                          const theme = e.target.checked ? "dark" : "light";
                          Setting.set("ui.theme", theme); // Update the theme globally
                          this.setState({ theme }); // Optionally update local state for immediate reflection
                        }}
                      />
                    }
                    label={item.title} // The label for Dark Mode
                    labelPlacement="start"
                  />
                </MUIBox>
              )}
            </div>
          ))}
        </MUIMenu>
        <DialogConfirm
          open={this.state.dialogResetPreferencesOpen}
          onClose={() => this.setState({ dialogResetPreferencesOpen: false })}
          onConfirm={() => {
            Setting.clearAll();
            window.location.reload();
          }}
          header="Reset Preferences"
          text="This will clear all saved preferences like queue configurations and report settings. Doing this can help if you are experiencing problems with Sales Pilot, but may require reconfiguring some things. Continue?"
        />
      </MUIBox>
    );
  }
}

export default withRouter(AppBar);
